import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Search, X, Plus, Grid3X3, List, Briefcase, MapPin, Calendar, Building } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { CLOSED_JOB_STATUSES_FILTER } from "@/utils/jobs/status";
import { SkillsFilter } from "@/components/candidates/search/SkillsFilter";
import { normalizeSkill } from "@/utils/skillNormalization";
import { cn } from "@/lib/utils";

interface JobRow {
	id: string;
	title: string;
	status?: string;
	seniority_level?: string;
	location_type?: string;
	hard_skills?: string[];
	application_deadline?: string | null;
	created_at?: string;
	company_name?: string;
}

interface AddJobDialogProps {
	isOpen: boolean;
	onClose: () => void;
	onAddJobs: (jobIds: string[]) => Promise<unknown> | void;
}

const statusBadgeClass = (status?: string) => {
	switch (status) {
		case "open":
			return "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800";
		case "hold":
			return "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800";
		case "closed/won":
		case "closed/lost":
			return "bg-gray-200 text-gray-700 border-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600";
		default:
			return "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800";
	}
};

const formatDeadline = (iso?: string | null): string | null => {
	if (!iso) return null;
	const d = new Date(iso);
	if (isNaN(d.getTime())) return null;
	return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

export const AddJobDialog = ({ isOpen, onClose, onAddJobs }: AddJobDialogProps) => {
	const { t } = useTranslation();
	const [jobs, setJobs] = useState<JobRow[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [searchTerm, setSearchTerm] = useState("");
	const [includeClosed, setIncludeClosed] = useState(false);
	const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
	const [selectedIds, setSelectedIds] = useState<string[]>([]);
	const [isAdding, setIsAdding] = useState(false);
	const [skillsFilter, setSkillsFilter] = useState<string[]>([]);
	const [skillsOperator, setSkillsOperator] = useState<"AND" | "OR">("AND");

	useEffect(() => {
		if (!isOpen) return;
		fetchJobs();
	}, [isOpen, includeClosed]);

	const fetchJobs = async () => {
		setIsLoading(true);
		setError(null);
		try {
			let query = supabase
				.from("jobs")
				.select("id, title, status, seniority_level, location_type, hard_skills, application_deadline, created_at, company:company_id (name)")
				.order("created_at", { ascending: false });

			if (!includeClosed)
				query = query.not("status", "in", CLOSED_JOB_STATUSES_FILTER);

			const { data, error: fetchError } = await query;
			if (fetchError) throw fetchError;

			const formatted: JobRow[] = (data as any[]).map((j) => ({
				id: j.id,
				title: j.title,
				status: j.status,
				seniority_level: j.seniority_level,
				location_type: j.location_type,
				hard_skills: Array.isArray(j.hard_skills) ? j.hard_skills : undefined,
				application_deadline: j.application_deadline,
				created_at: j.created_at,
				company_name: j.company ? j.company.name : undefined,
			}));
			setJobs(formatted);
		} catch (err: any) {
			console.error("Error fetching jobs:", err);
			setError(t("jobs.selector.error", "Failed to load jobs."));
		} finally {
			setIsLoading(false);
		}
	};

	const allSkills = useMemo(() => {
		const set = new Set<string>();
		jobs.forEach((j) => (j.hard_skills || []).forEach((s) => s && set.add(s)));
		return Array.from(set).sort();
	}, [jobs]);

	const filteredJobs = useMemo(() => {
		const q = searchTerm.trim().toLowerCase();
		const words = q ? q.split(/\s+/) : [];
		const normalizedSkillsFilter = skillsFilter.map((s) => normalizeSkill(s));

		return jobs.filter((j) => {
			if (words.length > 0) {
				const haystack = [j.title, j.company_name, j.seniority_level, j.location_type, ...(j.hard_skills || [])]
					.filter(Boolean)
					.join(" ")
					.toLowerCase();
				if (!words.every((w) => haystack.includes(w))) return false;
			}

			if (normalizedSkillsFilter.length > 0) {
				const jobSkills = (j.hard_skills || []).map((s) => normalizeSkill(s));
				const matches = skillsOperator === "AND"
					? normalizedSkillsFilter.every((fs) => jobSkills.includes(fs))
					: normalizedSkillsFilter.some((fs) => jobSkills.includes(fs));
				if (!matches) return false;
			}

			return true;
		});
	}, [jobs, searchTerm, skillsFilter, skillsOperator]);

	const toggleJob = (id: string) => {
		setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
	};

	const resetState = () => {
		setSelectedIds([]);
		setSearchTerm("");
		setIncludeClosed(false);
		setSkillsFilter([]);
		setSkillsOperator("AND");
	};

	const handleClose = () => {
		resetState();
		onClose();
	};

	const handleConfirm = async () => {
		if (selectedIds.length === 0) return;
		setIsAdding(true);
		try {
			await onAddJobs(selectedIds);
			resetState();
			onClose();
		} catch (err) {
			console.error("Error adding jobs:", err);
		} finally {
			setIsAdding(false);
		}
	};

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
			<DialogContent className="max-w-[95vw] w-[1400px] h-[90vh] flex flex-col [&>button]:hidden">
				<DialogHeader className="pb-4">
					<div className="flex items-center justify-between">
						<div>
							<DialogTitle className="text-xl font-semibold">
								{t("candidates.addJobModal.title", "Add Jobs to Candidate Process")}
							</DialogTitle>
							<p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
								{t("candidates.addJobModal.description", "Select one or more jobs to add to this candidate. Jobs are added to the 'Matched' stage.")}
							</p>
						</div>
					</div>
				</DialogHeader>

				{/* Search + Filters */}
				<div className="px-4 py-2">
					<div className="flex flex-col sm:flex-row gap-3">
						<div className="relative flex-1">
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
							<Input
								placeholder={t("candidates.addJobModal.searchPlaceholder", "Search jobs by title, company, skills...")}
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className="pl-10 pr-10 h-10"
							/>
							{searchTerm && (
								<Button
									type="button"
									variant="ghost"
									size="icon"
									className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 p-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
									onClick={() => setSearchTerm("")}
								>
									<X className="h-4 w-4" />
								</Button>
							)}
						</div>
						<div className="flex items-center gap-3">
							<SkillsFilter
								skillsFilter={skillsFilter}
								setSkillsFilter={setSkillsFilter}
								skillsOperator={skillsOperator}
								setSkillsOperator={setSkillsOperator}
								allSkills={allSkills}
							/>
							<label className="flex items-center gap-2 text-sm cursor-pointer select-none px-2">
								<Checkbox
									checked={includeClosed}
									onCheckedChange={(v) => setIncludeClosed(v === true)}
								/>
								{t("jobs.selector.includeClosed", "Include closed jobs")}
							</label>
						</div>
					</div>
				</div>

				{/* View Toggle */}
				<div className="flex flex-wrap gap-2 items-center justify-between px-4">
					<span className="text-sm text-gray-500 dark:text-gray-400">
						{isLoading
							? t("candidates.addJobModal.loading", "Loading jobs...")
							: t("candidates.addJobModal.resultCount", "{{count}} jobs", { count: filteredJobs.length })}
					</span>
					<div className="flex items-center gap-2">
						<span className="text-sm text-gray-500 dark:text-gray-400">{t("common.view", "View")}:</span>
						<ToggleGroup
							type="single"
							value={viewMode}
							onValueChange={(value) => value && setViewMode(value as "grid" | "list")}
							className="h-8 bg-gray-50 dark:bg-gray-800 rounded-sm p-1"
						>
							<ToggleGroupItem
								value="grid"
								className="h-6 w-6 p-0 data-[state=on]:bg-blue-100 data-[state=on]:text-blue-500"
							>
								<Grid3X3 className="h-4 w-4" />
							</ToggleGroupItem>
							<ToggleGroupItem
								value="list"
								className="h-6 w-6 p-0 data-[state=on]:bg-blue-100 data-[state=on]:text-blue-500"
							>
								<List className="h-4 w-4" />
							</ToggleGroupItem>
						</ToggleGroup>
					</div>
				</div>

				{/* Jobs list */}
				<div className="flex-1 overflow-y-auto border dark:border-gray-700 rounded-sm bg-gray-50 dark:bg-gray-800/50 min-h-0 mx-4 mb-1">
					{isLoading ? (
						<div className="p-8 text-center">
							<div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
							<p className="text-gray-500 dark:text-gray-400">
								{t("candidates.addJobModal.loading", "Loading jobs...")}
							</p>
						</div>
					) : error ? (
						<div className="p-8 text-center">
							<p className="text-red-600">{error}</p>
						</div>
					) : filteredJobs.length === 0 ? (
						<div className="p-8 text-center">
							<Briefcase className="h-16 w-16 text-gray-400 mx-auto mb-4" />
							<h3 className="text-lg font-semibold mb-2">
								{searchTerm
									? t("candidates.addJobModal.noResultsTitle", "No jobs found")
									: t("candidates.addJobModal.noJobsTitle", "No jobs available")}
							</h3>
							<p className="text-gray-500">
								{searchTerm
									? t("candidates.addJobModal.tryDifferentSearch", "Try a different search term")
									: t("candidates.addJobModal.noJobsHint", "Create a job first, or toggle 'Include closed jobs' to see archived ones.")}
							</p>
						</div>
					) : viewMode === "grid" ? (
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
							{filteredJobs.map((job) => {
								const selected = selectedIds.includes(job.id);
								const deadline = formatDeadline(job.application_deadline);
								return (
									<div
										key={job.id}
										className={cn(
											"relative bg-white dark:bg-gray-900 border rounded-sm cursor-pointer transition-all duration-200 hover:shadow-md p-4",
											selected
												? "ring-2 ring-inset ring-blue-500 border-blue-500"
												: "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
										)}
										onClick={() => toggleJob(job.id)}
									>
										<div className="absolute top-3 right-3">
											<Checkbox
												checked={selected}
												onCheckedChange={() => toggleJob(job.id)}
												onClick={(e) => e.stopPropagation()}
												className="h-4 w-4"
											/>
										</div>

										<div className="pr-8 mb-3">
											<div className="flex items-start gap-2 mb-1">
												<h3 className="font-semibold text-gray-900 dark:text-gray-100 leading-tight">{job.title}</h3>
											</div>
											{job.company_name && (
												<div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
													<Building className="h-3 w-3 mr-1 flex-shrink-0" />
													<span className="truncate">{job.company_name}</span>
												</div>
											)}
										</div>

										<div className="flex flex-wrap items-center gap-2 mb-3">
											{job.status && (
												<Badge variant="outline" className={cn("text-xs", statusBadgeClass(job.status))}>
													{job.status}
												</Badge>
											)}
											{job.seniority_level && (
												<Badge variant="outline" className="text-xs">{job.seniority_level}</Badge>
											)}
											{job.location_type && (
												<Badge variant="outline" className="text-xs flex items-center gap-1">
													<MapPin className="h-3 w-3" />
													{job.location_type}
												</Badge>
											)}
										</div>

										{job.hard_skills && job.hard_skills.length > 0 && (
											<div className="mb-3">
												<div className="flex flex-wrap gap-1">
													{job.hard_skills.slice(0, 4).map((skill, i) => (
														<Badge key={i} variant="outline" className="text-xs">{skill}</Badge>
													))}
													{job.hard_skills.length > 4 && (
														<Badge variant="outline" className="text-xs">+{job.hard_skills.length - 4}</Badge>
													)}
												</div>
											</div>
										)}

										{deadline && (
											<div className="flex items-center text-xs text-gray-500">
												<Calendar className="h-3 w-3 mr-1" />
												{t("candidates.addJobModal.deadline", "Deadline")}: {deadline}
											</div>
										)}
									</div>
								);
							})}
						</div>
					) : (
						<div className="space-y-2 p-4">
							{filteredJobs.map((job) => {
								const selected = selectedIds.includes(job.id);
								return (
									<div
										key={job.id}
										className={cn(
											"flex items-center space-x-4 bg-white dark:bg-gray-900 border rounded-sm cursor-pointer transition-all duration-200 hover:shadow-sm p-4",
											selected
												? "ring-2 ring-inset ring-blue-500 border-blue-500"
												: "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
										)}
										onClick={() => toggleJob(job.id)}
									>
										<Checkbox
											checked={selected}
											onCheckedChange={() => toggleJob(job.id)}
											onClick={(e) => e.stopPropagation()}
											className="h-4 w-4"
										/>
										<div className="flex-1 min-w-0">
											<div className="flex items-center gap-2 min-w-0">
												<h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate">{job.title}</h3>
												{job.status && (
													<Badge variant="outline" className={cn("text-xs shrink-0", statusBadgeClass(job.status))}>
														{job.status}
													</Badge>
												)}
											</div>
											{job.company_name && (
												<p className="text-sm text-gray-600 dark:text-gray-400 truncate">{job.company_name}</p>
											)}
										</div>
										<div className="hidden md:flex items-center gap-2 text-xs text-gray-500 shrink-0">
											{job.seniority_level && <span>{job.seniority_level}</span>}
											{job.location_type && (
												<span className="flex items-center gap-1">
													<MapPin className="h-3 w-3" />
													{job.location_type}
												</span>
											)}
										</div>
										{job.hard_skills && job.hard_skills.length > 0 && (
											<div className="hidden lg:flex flex-wrap gap-1 max-w-xs justify-end">
												{job.hard_skills.slice(0, 3).map((skill, i) => (
													<Badge key={i} variant="outline" className="text-xs">{skill}</Badge>
												))}
												{job.hard_skills.length > 3 && (
													<Badge variant="outline" className="text-xs">+{job.hard_skills.length - 3}</Badge>
												)}
											</div>
										)}
									</div>
								);
							})}
						</div>
					)}
				</div>

				{/* Footer */}
				<div className="border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-6 py-4 flex items-center justify-between flex-shrink-0">
					<div className="flex items-center space-x-4">
						{selectedIds.length > 0 && (
							<span className="text-sm text-gray-600 dark:text-gray-400">
								{t("candidates.addJobModal.selectedCount", "{{count}} selected", { count: selectedIds.length })}
							</span>
						)}
					</div>
					<div className="flex items-center space-x-2">
						<Button variant="outline" onClick={handleClose}>
							{t("common.cancel", "Cancel")}
						</Button>
						<Button
							onClick={handleConfirm}
							disabled={selectedIds.length === 0 || isAdding}
							className="bg-blue-500 hover:bg-blue-500/90"
						>
							{isAdding ? (
								<div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
							) : (
								<Plus className="h-4 w-4 mr-2" />
							)}
							{t("candidates.addJobModal.addJobs", "Add {{count}} jobs", { count: selectedIds.length })}
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
};
